import { Space, Tooltip, notification } from "antd"
import { faEnvelope, faEye, faFile, faLink, faPenToSquare } from "@fortawesome/pro-solid-svg-icons"
import IconAndTextButton from "../../IconAndTextButton"
import { Proposal, ProposalStatuses, currentProposalAttachmentId } from "./proposalsModel"
import { useCreateAttachmentUrlMutation, useCreateExistingProposalLinkMutation } from "./proposalsQueries"

type Props = {
  proposal: Proposal
  canSend: boolean
  onViewLink: (link: string) => void
  onSend: (proposal: Proposal) => void
  onPreview: (proposal: Proposal) => void
  onManage: (proposal: Proposal) => void
}

const ProposalVersionActions = ({ proposal, canSend, onViewLink, onSend, onPreview, onManage }: Props) => {
  const createExistingProposalLink = useCreateExistingProposalLinkMutation(onViewLink)
  const createAttachmentUrl = useCreateAttachmentUrlMutation((url: string) => {
    if (!url) {
      notification.warning({ message: 'The proposal PDF could not be found. It may have been deleted.' })
      return
    }
    setTimeout(() => window.open(url, '_blank'), 0)
  })

  const attachmentId = currentProposalAttachmentId(proposal)

  const isArchived = proposal.status === ProposalStatuses.Archived
  const isDraft = proposal.status === ProposalStatuses.Draft

  const sendTooltip = canSend ? undefined : 'Sending is disabled because the customer is inactive.'
  const pdfTooltip = attachmentId
    ? 'View the proposal PDF'
    : 'No PDF has been generated for this proposal yet.'

  return <Space style={{ marginBottom: '12px' }} wrap>
    {!isArchived && isDraft && <IconAndTextButton
      icon={faPenToSquare}
      text="Manage Proposal"
      onClick={() => onManage(proposal)}
    />}
    {!isArchived && <Tooltip title={sendTooltip}>
      {/* antd tooltips need a non-disabled wrapper to receive hover events. */}
      <span>
        <IconAndTextButton
          type="primary"
          icon={faEnvelope}
          text={isDraft ? 'Send Proposal' : 'Resend Proposal'}
          disabled={!canSend}
          onClick={() => onSend(proposal)}
        />
      </span>
    </Tooltip>}
    {!isArchived && <IconAndTextButton
      icon={faEye}
      text="Preview"
      onClick={() => onPreview(proposal)}
    />}
    <Tooltip title={pdfTooltip}>
      <span>
        <IconAndTextButton
          icon={faFile}
          text="View PDF"
          disabled={!attachmentId}
          loading={createAttachmentUrl.isLoading}
          onClick={() => attachmentId && createAttachmentUrl.mutate(attachmentId)}
        />
      </span>
    </Tooltip>
    <IconAndTextButton
      icon={faLink}
      text="View Link"
      loading={createExistingProposalLink.isLoading}
      onClick={() => createExistingProposalLink.mutate(proposal.id)}
    />
  </Space>
}

export default ProposalVersionActions
